"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
Object.defineProperty(exports, "__esModule", { value: true });
exports.AzureZipDeployImpl = void 0;
const tslib_1 = require("tslib");
/**
 * @author FanH <Siglud@gmail.com>
 */
const azureDeployImpl_1 = require("./azureDeployImpl");
const messages_1 = require("../../../../messages");
const deployConstant_1 = require("../../../../constant/deployConstant");
const crypto_1 = require("crypto");
const axios_1 = tslib_1.__importDefault(require("axios"));
const commonConstant_1 = require("../../../../constant/commonConstant");
const localizeUtils_1 = require("../../../../../common/localizeUtils");
const path_1 = tslib_1.__importDefault(require("path"));
const fileOperation_1 = require("../../../../utils/fileOperation");
const deploy_1 = require("../../../../../error/deploy");
const globalVars_1 = require("../../../../../common/globalVars");
const hooks_1 = require("@feathersjs/hooks");
const fs_extra_1 = require("fs-extra");
const constants_1 = require("../../../../../common/constants");
class AzureZipDeployImpl extends azureDeployImpl_1.AzureDeployImpl {
    pattern = /\/subscriptions\/([^\/]*)\/resourceGroups\/([^\/]*)\/providers\/Microsoft.Web\/sites\/([^\/]*)/i;
    serviceName;
    helpLink;
    summaries;
    summaryPrepare;
    constructor(args, context, serviceName, helpLink, summaries, summaryPrepare) {
        super(args, context);
        this.helpLink = helpLink;
        this.serviceName = serviceName;
        this.summaries = () => summaries.map((summary) => (0, localizeUtils_1.getLocalizedString)(summary, this.distDirectory));
        this.summaryPrepare = () => summaryPrepare.map((summary) => (0, localizeUtils_1.getLocalizedString)(summary, this.zipFilePath));
    }
    async azureDeploy(args, azureResource, azureCredential) {
        const cost = await this.zipDeploy(args, azureResource, azureCredential);
        await this.restartFunctionApp(azureResource);
        if (cost > deployConstant_1.DeployConstant.DEPLOY_OVER_TIME) {
            this.context.logProvider?.info((0, localizeUtils_1.getLocalizedString)("driver.deploy.notice.deployAcceleration", "https://aka.ms/teamsfx-config-run-from-package"));
        }
    }
    prepare = async (args) => {
        await this.packageToZip(args, this.context);
    };
    /**
     * deploy to azure app service or azure function use zip deploy method
     * @param args local file needed to be deployed
     * @param azureResource azure resource info
     * @param azureCredential azure user login credential
     * @return the zip deploy time cost
     * @protected
     */
    async zipDeploy(args, azureResource, azureCredential) {
        const zipBuffer = await this.packageToZip(args, this.context);
        this.context.logProvider.debug("Start to get Azure account info for deploy");
        const config = await this.createAzureDeployConfig(azureResource, azureCredential);
        this.context.logProvider.debug("Get Azure account info for deploy complete");
        const endpoint = await AzureZipDeployImpl.getZipDeployEndpoint(azureResource, config);
        this.context.logProvider.debug(`Start to upload code to ${endpoint}`);
        const startTime = Date.now();
        const location = await this.zipDeployPackage(endpoint, zipBuffer, config, this.context.logProvider);
        this.context.logProvider.debug("Upload code to Azure complete");
        this.context.logProvider.debug("Start to check Azure deploy status");
        const deployRes = await this.checkDeployStatus(location, config, this.context.logProvider);
        this.context.logProvider.debug("Check Azure deploy status complete");
        const cost = Date.now() - startTime;
        const telemetryData = {
            time_cost: cost.toString(),
            status: deployRes?.status?.toString() ?? "",
            message: deployRes?.message ?? "",
            received_time: deployRes?.received_time ?? "",
            started_time: deployRes?.start_time?.toString() ?? "",
            end_time: deployRes?.end_time?.toString() ?? "",
            last_success_end_time: deployRes?.last_success_end_time?.toString() ?? "",
            complete: deployRes?.complete?.toString() ?? "",
            active: deployRes?.active?.toString() ?? "",
            is_readonly: deployRes?.is_readonly?.toString() ?? "",
            site_name_hash: deployRes?.site_name
                ? (0, crypto_1.createHash)("sha256").update(deployRes.site_name).digest("hex")
                : "",
        };
        this.context.logProvider.verbose(`Start send telemetry data ${JSON.stringify(telemetryData)}`);
        this.context.telemetryReporter.sendTelemetryEvent("deployResponse", telemetryData);
        return cost;
    }
    /**
     * pack dist folder into zip
     * @param args dist folder and ignore files
     * @param context log provider etc..
     * @protected
     */
    async packageToZip(args, context) {
        const ig = await this.handleIgnore(args, context);
        this.zipFilePath = this.zipFilePath
            ? path_1.default.isAbsolute(this.zipFilePath)
                ? this.zipFilePath
                : path_1.default.join(this.workingDirectory, this.zipFilePath)
            : path_1.default.join(this.workingDirectory, deployConstant_1.DeployConstant.DEPLOYMENT_TMP_FOLDER, deployConstant_1.DeployConstant.DEPLOYMENT_ZIP_CACHE_FILE);
        this.context.logProvider?.debug(`start zip dist folder ${this.distDirectory}`);
        const res = await (0, fileOperation_1.zipFolderAsync)(this.distDirectory, this.zipFilePath, ig);
        this.context.logProvider?.debug(`zip dist folder ${this.distDirectory} to ${this.zipFilePath} complete`);
        return res;
    }
    /**
     * call azure app service or azure function zip deploy method
     * @param zipDeployEndpoint azure zip deploy endpoint
     * @param zipBuffer zip file buffer
     * @param config azure upload config, including azure account credential
     * @param logger log provider
     * @protected
     */
    async zipDeployPackage(zipDeployEndpoint, zipBuffer, config, logger) {
        let res;
        let retryCount = 0;
        while (true) {
            this.context.logProvider.verbose(`Start to upload zip file to ${zipDeployEndpoint}`);
            try {
                res = await azureDeployImpl_1.AzureDeployImpl.AXIOS_INSTANCE.post(zipDeployEndpoint, zipBuffer, config);
                this.context.logProvider.verbose(`Upload zip file to ${zipDeployEndpoint} complete, response: ${JSON.stringify(res, Object.getOwnPropertyNames(res))}.`);
                break;
            }
            catch (e) {
                this.context.logProvider.verbose(`Upload zip file failed with error: ${JSON.stringify(e, Object.getOwnPropertyNames(e), 2)}`);
                if (axios_1.default.isAxiosError(e)) {
                    // if the error is remote server error, retry
                    if ((e.response?.status ?? commonConstant_1.HttpStatusCode.OK) >= commonConstant_1.HttpStatusCode.INTERNAL_SERVER_ERROR) {
                        retryCount += 1;
                        if (retryCount < deployConstant_1.DeployConstant.DEPLOY_UPLOAD_RETRY_TIMES) {
                            logger.warning(`Upload zip file failed with response status code: ${e.response?.status ?? "NA"}. Retrying...`);
                        }
                        else {
                            // if retry times exceed, throw error
                            logger.warning(`Retry times exceeded. Upload zip file failed with remote server error. Message: ${JSON.stringify(e.response?.data)}`);
                            throw new deploy_1.DeployZipPackageError(zipDeployEndpoint, e, this.helpLink);
                        }
                    }
                    else {
                        // None server error, throw
                        logger.error(`Upload zip file failed with response status code: ${e.response?.status ?? "NA"}, message: ${JSON.stringify(e.response?.data)}`);
                        throw new deploy_1.DeployZipPackageError(zipDeployEndpoint, e, this.helpLink);
                    }
                }
                else {
                    // if the error is not axios error, throw
                    logger.error(`Upload zip file failed with error: ${JSON.stringify(e)}`);
                    throw new deploy_1.DeployZipPackageError(zipDeployEndpoint, e, this.helpLink);
                }
            }
            finally {
                zipBuffer.destroy();
            }
        }
        if (res?.status !== commonConstant_1.HttpStatusCode.OK && res?.status !== commonConstant_1.HttpStatusCode.ACCEPTED) {
            if (res?.status) {
                logger.error(`Deployment is failed with error code: ${res.status}.`);
            }
            throw new deploy_1.DeployZipPackageError(zipDeployEndpoint, new Error(`status code: ${res?.status ?? "NA"}`), this.helpLink);
        }
        return res.headers.location;
    }
    /**
     * create azure zip deploy endpoint
     * @param azureResource resource id of the website
     * @param config azure upload config, including azure account credential
     */
    static async getZipDeployEndpoint(azureResource, config) {
        const response = await fetch(`${(0, constants_1.getResourceServiceEndpoint)(constants_1.ResourceServiceType.Azure)}/subscriptions/${azureResource.subscriptionId}/resourceGroups/${azureResource.resourceGroupName}/providers/Microsoft.Web/sites/${azureResource.instanceId}?api-version=2024-04-01`, {
            headers: config.headers,
        });
        const responseData = await response.json();
        const hostNames = responseData.properties.enabledHostNames;
        const scmHostName = hostNames.find((host) => host.includes("scm"));
        if (!scmHostName) {
            throw new Error(`Cannot find SCM host name. Available host names: ${hostNames.join(", ")}`);
        }
        return `https://${scmHostName}/api/zipdeploy?isAsync=true`;
    }
    updateProgressbar() {
        this.progressBar?.next(messages_1.ProgressMessages.deployToAzure(this.workingDirectory, this.serviceName));
    }
}
exports.AzureZipDeployImpl = AzureZipDeployImpl;
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "Azure", component: "AzureZipDeployImpl" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, fs_extra_1.ReadStream, Object, Object]),
    tslib_1.__metadata("design:returntype", Promise)
], AzureZipDeployImpl.prototype, "zipDeployPackage", null);
//# sourceMappingURL=AzureZipDeployImpl.js.map